import { useEffect, useState, ChangeEvent } from 'react';
import { useLocalization } from '~/framework';
import { FormLabel, Select, Stack, FormHelperText, FormControl, Text } from '@chakra-ui/react';
import { mapValues } from '@dozuki/web-js/utils';
import { RequiredAsterisk } from '.';
import { QuestionTooltip } from '~/ui-components';

type MonthUnit = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

type SelectMonthAndDayFormControlProps = {
   readonly month: number;
   readonly setMonth: (month: number) => void;
   readonly setMonthName: (monthName: string) => void;
   readonly day: number;
   readonly setDay: (day: number) => void;
   readonly year: number;
   readonly setYear: (year: number) => void;
   readonly initialYear: number;
   readonly controlWidth: number;
   readonly label: string;
   readonly errorMessage?: string;
   readonly showYear?: boolean;
};

export const daysInMonth = (year: number, month: number) => new Date(year, month, 0).getDate();

export function SelectMonthAndDayFormControl({
   month,
   setMonth,
   setMonthName,
   day,
   setDay,
   year,
   setYear,
   initialYear,
   controlWidth,
   label,
   errorMessage,
   showYear,
}: Readonly<SelectMonthAndDayFormControlProps>) {
   const { _js } = useLocalization();
   const AllMonths: Record<MonthUnit, string> = {
      1: _js('January'),
      2: _js('February'),
      3: _js('March'),
      4: _js('April'),
      5: _js('May'),
      6: _js('June'),
      7: _js('July'),
      8: _js('August'),
      9: _js('September'),
      10: _js('October'),
      11: _js('November'),
      12: _js('December'),
   };
   const [maxMonthDays, setMaxMonthDays] = useState(day);

   const adjustDaysForMonth = (month: number) => {
      if (month == 0) {
         return;
      }

      const nonLeapYear = 2023;
      const newMaxMonthDays = daysInMonth(nonLeapYear, month);
      setMaxMonthDays(newMaxMonthDays);

      if (day > newMaxMonthDays) {
         setDay(newMaxMonthDays);
      }
   };

   const onChangeYear = (e: ChangeEvent<HTMLSelectElement>) => {
      const value = parseInt(e.target.value);
      setYear(value);
   };

   const onChangeMonth = (e: ChangeEvent<HTMLSelectElement>) => {
      const value = parseInt(e.target.value);
      setMonth(value);
      setMonthName(e.target.selectedOptions[0].text);
   };

   const onChangeDay = (e: ChangeEvent<HTMLSelectElement>) => {
      const value = parseInt(e.target.value);
      setDay(value);
   };

   useEffect(() => {
      adjustDaysForMonth(month);
      setMonthName(AllMonths[month as MonthUnit]);
   }, [month]);

   return (
      <FormControl isInvalid={Boolean(errorMessage)} id="date-selector" mb={5}>
         <FormLabel htmlFor="date-selector" m={2} mb={0} fontWeight={'bold'}>
            <Stack direction="row" mb={0} gap={1}>
               <Text>{label}</Text>
               <RequiredAsterisk />
               {showYear && (
                  <QuestionTooltip
                     label={_js(
                        'If a publication before this date meets the review requirement, the next due date will be set N days after the publication date'
                     )}
                     placement="right-end"
                     boxposition={'relative'}
                     boxtop={0}
                     boxright={0}
                  />
               )}
            </Stack>
         </FormLabel>
         <Stack direction="row" maxW={controlWidth} gap={2}>
            <Select
               name="month"
               value={month}
               onChange={onChangeMonth}
               mt={2}
               cursor={'pointer'}
               placeholder={month ? '' : _js('Month')}
               aria-label={_js('Select the month')}
            >
               {mapValues(AllMonths, (monthName: string, monthNumber: MonthUnit) => (
                  <option value={monthNumber} key={monthName}>
                     {monthName}
                  </option>
               ))}
            </Select>
            <Select
               name="day"
               value={day}
               onChange={onChangeDay}
               mt={2}
               cursor={'pointer'}
               placeholder={day ? '' : _js('Day')}
               aria-label={_js('Select the day')}
            >
               {mapValues(
                  Array.from(Array(maxMonthDays ? maxMonthDays + 1 : 0).keys()).slice(1),
                  (day: any) => (
                     <option value={day} key={day}>
                        {day}
                     </option>
                  )
               )}
            </Select>
            {showYear && (
               <Select
                  name="year"
                  value={year}
                  onChange={onChangeYear}
                  mt={2}
                  cursor={'pointer'}
                  placeholder={year ? '' : _js('Year')}
                  aria-label={_js('Select the year')}
               >
                  {mapValues(Array(initialYear, initialYear + 1, initialYear + 2), (year: any) => (
                     <option value={year} key={year}>
                        {year}
                     </option>
                  ))}
               </Select>
            )}
         </Stack>
         {errorMessage && (
            <FormHelperText color={'red'} fontWeight={'bold'} m={4} mt={2}>
               {errorMessage}
            </FormHelperText>
         )}
      </FormControl>
   );
}
